<template>
  <!-- Autocomplete générique, calqué sur AgencyAutocomplete. Différence de fond : la liste
       ne vient pas d'un référentiel que le composant saurait charger, mais des options déjà
       calculées par la vue hôte (le demandeur, par exemple, dépend de l'agence saisie). Sert
       partout où un <select> afficherait trop d'options : ouvrir le champ n'en propose que
       les premières (cf. LIMIT), le reste se trouve à la frappe. -->
  <v-select
    :value="selectedOption"
    :options="results"
    :filterable="false"
    label="label"
    :placeholder="placeholder"
    :disabled="disabled || !index.length"
    class="options-ac"
    :class="'options-ac--' + variant"
    @input="onInput"
    @search="onSearch"
  >
    <template slot="option" slot-scope="opt">
      <!-- Intitulé de groupe, porté par la première option du groupe : vue-select ne connaît
           pas les groupes. Un clic dessus ne sélectionne rien. -->
      <div v-if="opt.groupStart" class="options-ac__group" @mousedown.stop.prevent>{{ opt.group }}</div>
      <div class="options-ac__opt" :class="{ 'options-ac__opt--grouped': grouped }">{{ opt.label }}</div>
    </template>

    <template slot="selected-option" slot-scope="opt">
      <span class="options-ac__label">{{ opt.selectedLabel || opt.label }}</span>
    </template>

    <template slot="no-options" slot-scope="{ search }">
      <div class="options-ac__empty">
        <template v-if="normalize(search).trim()">Aucun résultat pour «&nbsp;{{ search }}&nbsp;»</template>
        <template v-else>Aucune option disponible</template>
      </div>
    </template>

    <li v-if="hiddenCount > 0" slot="list-footer" class="options-ac__more">
      <i class="far fa-ellipsis-h"></i> {{ hiddenCount.toLocaleString("fr-FR") }} autre(s) —
      {{ currentQuery ? "précisez la recherche" : "saisissez pour rechercher" }}
    </li>
  </v-select>
</template>

<script>
// Nombre d'options rendues dans le menu par défaut, aussi bien à l'ouverture du champ que pour
// les résultats d'une recherche (prop `limit`).
const LIMIT = 20;

// Tri des libellés. Un Intl.Collator réutilisé va nettement plus vite que localeCompare
// appelé à chaque comparaison, et la liste des demandeurs dépasse les 20 000 entrées.
const collator = new Intl.Collator("fr", { sensitivity: "base", numeric: true });

// Minuscule + suppression des accents, pour un matching tolérant.
function normalize(s) {
  return (s == null ? "" : String(s))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // marques diacritiques combinantes
}

export default {
  name: "OptionsAutocomplete",
  props: {
    value: { default: null },
    // Options au format des <select> du back-office : [{ value, text }].
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: "Recherchez en saisissant les premiers caractères…" },
    disabled: { type: Boolean, default: false },
    // 'filter' : champ compact d'une ligne de filtres. 'form' : le champ reprend
    // l'habillage global des champs de formulaire.
    variant: { type: String, default: "filter" },
    // Regroupe les options sous l'intitulé de leur `group` ([{ value, text, group, selectedText }]).
    // La recherche porte aussi sur le groupe ; `selectedText` est le libellé affiché une fois choisi.
    grouped: { type: Boolean, default: false },
    limit: { type: Number, default: LIMIT },
  },
  data() {
    return {
      results: [],
      hiddenCount: 0,
      // Dernière expression recherchée : distingue « le champ est vide » de « la recherche
      // n'a rien donné », pour le pied de liste.
      currentQuery: "",
    };
  },
  computed: {
    /**
     * Index normalisé, recalculé seulement si les options changent.
     *
     * Trié par libellé : les options arrivent dans l'ordre de la base (les utilisateurs, par
     * exemple, ne sont triés nulle part), et « les premières » ne voudrait alors rien dire.
     */
    index() {
      return this.options
        .map((o) => ({
          value: o.value,
          label: o.text == null ? "" : String(o.text),
          group: o.group == null ? "" : String(o.group),
          selectedLabel: o.selectedText || null,
          hay: normalize(this.grouped ? (o.group || "") + " " + o.text : o.text),
        }))
        .sort((a, b) => (this.grouped ? collator.compare(a.group, b.group) : 0) || collator.compare(a.label, b.label));
    },
    // Cherchée dans l'index complet et non dans `results`, pour que le libellé s'affiche
    // même hors résultats filtrés. Comparaison souple : la valeur peut arriver sous forme
    // de chaîne.
    selectedOption() {
      if (this.value == null || this.value === "") return null;
      return this.index.find((o) => String(o.value) === String(this.value)) || null;
    },
  },
  watch: {
    // Les options peuvent changer après le montage — la liste des demandeurs est rechargée
    // à chaque changement d'agence.
    index() {
      this.computeResults(this.currentQuery);
    },
  },
  mounted() {
    this.computeResults("");
  },
  methods: {
    normalize,
    onInput(option) {
      this.$emit("input", option ? option.value : null);
    },
    /**
     * v-select émet `search` à chaque frappe, et aussi à la remise à zéro après sélection.
     * On ne s'accroche volontairement pas à `open` : cet événement part APRÈS `search`
     * lorsque la frappe ouvre le menu, et y recalculer la liste effacerait le filtrage.
     */
    onSearch(search) {
      this.currentQuery = normalize(search).trim();
      this.computeResults(this.currentQuery);
    },
    computeResults(q) {
      if (!q) {
        this.hiddenCount = Math.max(0, this.index.length - this.limit);
        this.results = this.markGroups(this.index.slice(0, this.limit));
        return;
      }

      const tokens = q.split(/\s+/);
      const scored = [];
      for (const item of this.index) {
        const s = this.scoreItem(item, tokens);
        if (s > 0) scored.push({ item, s });
      }
      scored.sort((a, b) => b.s - a.s || collator.compare(a.item.label, b.item.label));
      this.hiddenCount = Math.max(0, scored.length - this.limit);
      const items = scored.slice(0, this.limit).map((x) => x.item);
      // Groupés, les résultats restent contigus par groupe, dans l'ordre du meilleur de chaque groupe.
      if (this.grouped) {
        const rank = {};
        items.forEach((item, i) => {
          if (!(item.group in rank)) rank[item.group] = i;
        });
        items.sort((a, b) => rank[a.group] - rank[b.group]);
      }
      this.results = this.markGroups(items);
    },
    // Copie des options, la première de chaque groupe portant l'intitulé (`groupStart`).
    markGroups(items) {
      if (!this.grouped) return items;
      return items.map((item, i) => ({ ...item, groupStart: i === 0 || item.group !== items[i - 1].group }));
    },
    // Tous les mots de la requête doivent matcher ; le score total classe.
    scoreItem(item, tokens) {
      let total = 0;
      for (const t of tokens) {
        const s = this.scoreToken(item, t);
        if (s <= 0) return 0;
        total += s;
      }
      return total;
    },
    // Priorité : préfixe > sous-chaîne (plus tôt = mieux) > sous-séquence.
    scoreToken(item, t) {
      const idx = item.hay.indexOf(t);
      if (idx === 0) return 100;
      if (idx > 0) return 60 - Math.min(idx, 30);
      return this.isSubsequence(item.hay, t) ? 15 : 0;
    },
    isSubsequence(hay, t) {
      let h = 0;
      for (let i = 0; i < t.length; i++) {
        h = hay.indexOf(t[i], h);
        if (h === -1) return false;
        h++;
      }
      return true;
    },
  },
};
</script>

<style scoped>
/* Racine bornée à la largeur de la colonne, sinon le toggle grandit avec le
   contenu et la valeur déborde du bloc (l'ellipsis ne peut pas se déclencher). */
.options-ac {
  width: 100%;
}
.options-ac >>> .vs__dropdown-toggle {
  background: #fff;
  max-width: 100%;
  align-items: stretch;
}
.options-ac >>> .vs__selected-options {
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
  padding: 0 2px;
}
.options-ac >>> .vs__selected {
  margin: 0 2px;
  max-width: 100%;
  flex-wrap: nowrap;
  overflow: hidden;
}
/* Valeur sélectionnée : forcée sur une seule ligne, tronquée par « … ». */
.options-ac >>> .vs__selected .options-ac__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.options-ac >>> .vs__search {
  margin: 0;
}
.options-ac >>> .vs__actions {
  padding-top: 0;
}
.options-ac >>> .vs__dropdown-menu {
  padding: 4px 0;
}
.options-ac >>> .vs__dropdown-option {
  padding: 6px 12px;
}
.options-ac >>> .vs__dropdown-option--highlight {
  background: #1ab394;
  color: #fff;
}

/* Ligne de filtres : champ compact, à la hauteur des autres filtres. */
.options-ac--filter >>> .vs__dropdown-toggle {
  border-color: #e5e6e7;
  border-radius: 3px;
  min-height: 31px;
  padding: 0; /* défaut asymétrique 0 0 4px → casse le centrage */
}
.options-ac--filter >>> .vs__selected,
.options-ac--filter >>> .vs__search {
  font-size: 13px;
}
.options-ac--filter .options-ac__opt {
  font-size: 13px;
}

/* Formulaire : hauteur, bordure et retrait de 20px viennent de l'habillage global des
   .vs__dropdown-toggle. On ne corrige que le padding vertical asymétrique de vue-select,
   qui décentrerait la valeur. */
.options-ac--form >>> .vs__dropdown-toggle {
  padding: 0 0 0 20px;
}
/* Le menu déroulant est hors du champ : il n'hérite pas des 12px du formulaire mais des
   10px du body. */
.options-ac--form .options-ac__opt {
  font-size: 12px;
}

.options-ac__opt {
  white-space: normal;
  color: #34495e;
}
.options-ac >>> .vs__dropdown-option--highlight .options-ac__opt {
  color: #fff;
}
.options-ac__label {
  color: #34495e;
}
/* Intitulé de groupe : bandeau gris en tête de la première option du groupe, qui garde son
   fond même quand l'option est survolée. */
.options-ac__group {
  margin: -6px -12px 6px;
  padding: 6px 12px 4px;
  background: #f7f7f7;
  color: #999;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  cursor: default;
}
.options-ac__opt--grouped {
  padding-left: 10px;
}
.options-ac__empty {
  padding: 6px 4px;
  color: #909499;
  font-size: 13px;
}
.options-ac__more {
  list-style: none;
  padding: 6px 12px;
  font-size: 12px;
  color: #909499;
  border-top: 1px solid #f1f3f5;
  background: #fafbfc;
}
.options-ac__more i {
  margin-right: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/Global/OptionsAutocomplete.vue